import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

// Local types for the Supabase-based implementation
interface TrendDatapoint {
  date: string
  value: number
}

interface DashboardTrends {
  metric: string
  period: string
  granularity: string
  datapoints: TrendDatapoint[]
}

interface DashboardTrendsQuery {
  metric: 'mrr' | 'organizations' | 'users' | 'churn'
  period?: string
  granularity?: 'day' | 'week' | 'month'
}

function getDateRange(period: string): { start: Date; end: Date } {
  const end = new Date()
  const start = new Date()

  switch (period) {
    case '7d':
      start.setDate(end.getDate() - 7)
      break
    case '30d':
      start.setDate(end.getDate() - 30)
      break
    case '90d':
      start.setDate(end.getDate() - 90)
      break
    case '1y':
      start.setFullYear(end.getFullYear() - 1)
      break
    default:
      start.setDate(end.getDate() - 30)
  }

  return { start, end }
}

export function useDashboardTrends(params: DashboardTrendsQuery) {
  return useQuery<DashboardTrends>({
    queryKey: ['super-admin', 'dashboard', 'trends', params],
    queryFn: async () => {
      const { start, end } = getDateRange(params.period || '30d')
      const granularity = params.granularity || 'day'

      const datapoints: TrendDatapoint[] = []

      if (params.metric === 'organizations') {
        // Get organizations created per period
        const { data: orgs } = await supabase
          .from('organizations')
          .select('created_at')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString())
          .order('created_at', { ascending: true })

        // Group by granularity
        const grouped: Record<string, number> = {}
        for (const org of orgs || []) {
          const date = new Date(org.created_at)
          let key: string

          if (granularity === 'day') {
            key = date.toISOString().split('T')[0] || ''
          } else if (granularity === 'week') {
            const weekStart = new Date(date)
            weekStart.setDate(date.getDate() - date.getDay())
            key = weekStart.toISOString().split('T')[0] || ''
          } else {
            key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
          }

          if (key) {
            grouped[key] = (grouped[key] || 0) + 1
          }
        }

        for (const [date, value] of Object.entries(grouped)) {
          datapoints.push({ date, value })
        }
      } else if (params.metric === 'mrr') {
        // Get MRR over time - simplified
        const { data: subs } = await supabase
          .from('subscriptions')
          .select('created_at, subscription_plans(price_monthly_cents)')
          .gte('created_at', start.toISOString())
          .lte('created_at', end.toISOString())
          .eq('status', 'active')
          .order('created_at', { ascending: true })

        const grouped: Record<string, number> = {}
        for (const sub of subs || []) {
          const date = new Date(sub.created_at)
          const key = date.toISOString().split('T')[0] || ''
          const price = (sub as any).subscription_plans?.price_monthly_cents || 0
          if (key) {
            grouped[key] = (grouped[key] || 0) + price
          }
        }

        for (const [date, value] of Object.entries(grouped)) {
          datapoints.push({ date, value })
        }
      }

      return {
        metric: params.metric,
        period: params.period || '30d',
        granularity,
        datapoints,
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!params.metric,
  })
}
